import { createSignal, createEffect, For, Show, type Component } from "solid-js";
import { createQuery, useQueryClient } from "@tanstack/solid-query";
import type { ColumnDef } from "@tanstack/solid-table";
import { PanelLayout, type PanelStatus, type SubTab } from "../shared/panel-base";
import { DataTable } from "../shared/data-table";
import { useConfirm } from "../shared/confirm-dialog";
import { registerPanel } from "../panel-registry";
import { apiFetch } from "../shared/api-fetch";

interface ScopePowershell {
  allowExec?: boolean;
  profilesOnly?: boolean;
}
interface ScopeSsh {
  targets?: string[];
  destructive?: boolean;
}
interface ScopeFs {
  roots?: string[];
  read?: boolean;
  write?: boolean;
}
interface ScopeSql {
  targets?: string[];
  selectOnly?: boolean;
}
interface ScopeRegistry {
  hives?: string[];
  read?: boolean;
  write?: boolean;
}
interface ScopeProcess {
  list?: boolean;
  kill?: boolean;
}
interface ScopeHandles {
  list?: boolean;
}

interface Scope {
  powershell?: ScopePowershell;
  ssh?: ScopeSsh;
  fs?: ScopeFs;
  sql?: ScopeSql;
  registry?: ScopeRegistry;
  process?: ScopeProcess;
  handles?: ScopeHandles;
}

interface McpToken {
  id: string;
  label: string;
  scope?: Scope;
  sessionCode?: string;
  expiresAt?: string;
  createdAt?: string;
  revoked?: boolean;
}

interface TokenListResp {
  items?: McpToken[];
}

interface SessionEntry {
  code: string;
  label: string;
}

interface SessionsRaw {
  items?: unknown[];
  sessions?: unknown[];
}

interface CreateTokenResp {
  ok?: boolean;
  token?: string;
  plainToken?: string;
  expiresAt?: string;
  error?: string;
}

const POLL_MS = 15_000;

function formatDate(value: string | undefined): string {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function scopeSummary(scope: Scope | undefined): string {
  if (!scope || typeof scope !== "object") return "(vide)";
  const parts: string[] = [];
  for (const k of Object.keys(scope) as (keyof Scope)[]) {
    const sec = (scope[k] || {}) as Record<string, unknown>;
    const bits: string[] = [];
    for (const sk of Object.keys(sec)) {
      const v = sec[sk];
      if (Array.isArray(v) && v.length) bits.push(`${sk}=${v.length}`);
      else if (v === true) bits.push(sk);
    }
    if (bits.length) parts.push(`${k}[${bits.join(",")}]`);
  }
  return parts.length ? parts.join(" ") : "(vide)";
}

async function fetchTokens(): Promise<McpToken[]> {
  const resp = await apiFetch("/mcp-admin/tokens?activeOnly=1", {
    method: "GET",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const data = (await resp.json()) as TokenListResp;
  return Array.isArray(data.items) ? data.items : [];
}

async function fetchSessionsList(): Promise<SessionEntry[]> {
  const resp = await apiFetch("/api/sessions", {
    method: "GET",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!resp.ok) return [];
  const data = (await resp.json()) as SessionsRaw | unknown[];
  const items: unknown[] = Array.isArray((data as SessionsRaw).items)
    ? ((data as SessionsRaw).items as unknown[])
    : Array.isArray((data as SessionsRaw).sessions)
      ? ((data as SessionsRaw).sessions as unknown[])
      : Array.isArray(data)
        ? (data as unknown[])
        : [];
  return items
    .map((raw): SessionEntry => {
      const s = raw as Record<string, unknown>;
      const meta = (s.meta as Record<string, unknown> | undefined) || {};
      const code = String(s.code || s.sessionCode || s.id || "");
      const label = String(s.label || meta.label || "");
      return { code, label };
    })
    .filter((s) => s.code);
}

interface ScopeFieldBool {
  kind: "bool";
  name: string;
  label: string;
}
interface ScopeFieldList {
  kind: "list";
  name: string;
  label: string;
  placeholder?: string;
}
type ScopeField = ScopeFieldBool | ScopeFieldList;

interface ScopeSectionDef {
  title: string;
  key: keyof Scope;
  fields: ScopeField[];
}

const SCOPE_SECTIONS: ScopeSectionDef[] = [
  {
    title: "PowerShell",
    key: "powershell",
    fields: [
      { kind: "bool", name: "allowExec", label: "allowExec" },
      { kind: "bool", name: "profilesOnly", label: "profilesOnly" },
    ],
  },
  {
    title: "SSH",
    key: "ssh",
    fields: [
      { kind: "list", name: "targets", label: "targets (1/ligne)" },
      { kind: "bool", name: "destructive", label: "destructive" },
    ],
  },
  {
    title: "FS",
    key: "fs",
    fields: [
      { kind: "list", name: "roots", label: "roots (1/ligne)" },
      { kind: "bool", name: "read", label: "read" },
      { kind: "bool", name: "write", label: "write" },
    ],
  },
  {
    title: "SQL",
    key: "sql",
    fields: [
      { kind: "list", name: "targets", label: "targets (1/ligne)" },
      { kind: "bool", name: "selectOnly", label: "selectOnly" },
    ],
  },
  {
    title: "Registry",
    key: "registry",
    fields: [
      { kind: "list", name: "hives", label: "hives (1/ligne, ex: HKLM:\\SOFTWARE)" },
      { kind: "bool", name: "read", label: "read" },
      { kind: "bool", name: "write", label: "write" },
    ],
  },
  {
    title: "Process",
    key: "process",
    fields: [
      { kind: "bool", name: "list", label: "list" },
      { kind: "bool", name: "kill", label: "kill" },
    ],
  },
  {
    title: "Handles",
    key: "handles",
    fields: [{ kind: "bool", name: "list", label: "list" }],
  },
];

const McpSessionsPanel: Component = () => {
  const confirmDialog = useConfirm();
  const qc = useQueryClient();

  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [plainToken, setPlainToken] = createSignal<{ token: string; expiresAt: string } | null>(null);
  const [copyLabel, setCopyLabel] = createSignal("Copier");

  // Create form state
  const [labelInput, setLabelInput] = createSignal("");
  const [ttlInput, setTtlInput] = createSignal(30);
  const [sessionCodeSel, setSessionCodeSel] = createSignal("");
  const [scopeBools, setScopeBools] = createSignal<Record<string, boolean>>({});
  const [scopeLists, setScopeLists] = createSignal<Record<string, string>>({});

  const tokensQuery = createQuery(() => ({
    queryKey: ["mcp-admin", "tokens"] as const,
    queryFn: fetchTokens,
    refetchInterval: POLL_MS,
    staleTime: 10_000,
  }));

  const sessionsQuery = createQuery(() => ({
    queryKey: ["api", "sessions"] as const,
    queryFn: fetchSessionsList,
    staleTime: 30_000,
  }));

  const status = (): PanelStatus => {
    if (actionStatus()) return actionStatus()!;
    if (tokensQuery.isError) return { text: "Erreur reseau", level: "danger" };
    if (tokensQuery.isLoading) return { text: "Chargement...", level: "info" };
    const items = tokensQuery.data || [];
    return { text: `${items.length} token(s) actif(s).`, level: "info" };
  };

  async function revokeToken(token: McpToken): Promise<void> {
    if (!token.id) return;
    const ok = await confirmDialog.confirm({
      title: "Revoquer token",
      body: `Saisir le label exact "${token.label}" pour confirmer la revocation.`,
      level: "danger",
      confirmLabel: "Revoquer",
      entityName: token.label,
    });
    if (!ok) return;
    try {
      const resp = await apiFetch(`/mcp-admin/tokens/${encodeURIComponent(token.id)}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
      if (!resp.ok) {
        setActionStatus({ text: `Echec revocation: ${resp.status}`, level: "danger" });
        return;
      }
      setActionStatus({ text: `Token ${token.label} revoque.`, level: "info" });
      qc.invalidateQueries({ queryKey: ["mcp-admin", "tokens"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionStatus({ text: `Erreur: ${msg}`, level: "danger" });
    }
  }

  function readScope(): Scope {
    const scope: Record<string, Record<string, unknown>> = {};
    for (const section of SCOPE_SECTIONS) {
      const key = String(section.key);
      const acc: Record<string, unknown> = {};
      for (const f of section.fields) {
        const id = `${key}.${f.name}`;
        if (f.kind === "bool") {
          if (scopeBools()[id]) acc[f.name] = true;
        } else {
          const raw = scopeLists()[id] || "";
          const lines = raw
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          if (lines.length) acc[f.name] = lines;
        }
      }
      if (Object.keys(acc).length > 0) scope[key] = acc;
    }
    return scope as Scope;
  }

  async function submitCreate(e: Event): Promise<void> {
    e.preventDefault();
    const label = labelInput().trim();
    if (!label) {
      setActionStatus({ text: "Label requis.", level: "warn" });
      return;
    }
    const ttlMinutes = Number(ttlInput()) || 30;
    const sessionCode = sessionCodeSel() || null;
    const scope = readScope();
    try {
      const resp = await apiFetch("/mcp-admin/tokens", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ label, ttlMinutes, sessionCode, scope }),
      });
      const data = (await resp.json().catch(() => ({}))) as CreateTokenResp;
      if (!resp.ok || !data.ok) {
        setActionStatus({
          text: `Echec creation: ${data.error || resp.status}`,
          level: "danger",
        });
        return;
      }
      setActionStatus({ text: `Token cree: ${label}`, level: "info" });
      setPlainToken({
        token: data.token || data.plainToken || "",
        expiresAt: data.expiresAt || "",
      });
      setCopyLabel("Copier");
      // reset form
      setLabelInput("");
      setTtlInput(30);
      setSessionCodeSel("");
      setScopeBools({});
      setScopeLists({});
      qc.invalidateQueries({ queryKey: ["mcp-admin", "tokens"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionStatus({ text: `Erreur: ${msg}`, level: "danger" });
    }
  }

  async function copyToken(): Promise<void> {
    const t = plainToken();
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t.token);
      setCopyLabel("Copie");
    } catch {
      setCopyLabel("Echec");
    }
  }

  // Refresh sessions when needed (no-op effect that ensures query is active)
  createEffect(() => {
    void sessionsQuery.data;
  });

  const tokenColumns: ColumnDef<McpToken>[] = [
    { header: "Label", accessorKey: "label" },
    {
      header: "Scope",
      id: "scope",
      accessorFn: (row) => scopeSummary(row.scope),
      cell: (info) => (
        <span style={{ "font-family": "monospace", "font-size": "11px" }}>{scopeSummary(info.row.original.scope)}</span>
      ),
    },
    { header: "Session", accessorKey: "sessionCode" },
    {
      header: "Expire",
      accessorKey: "expiresAt",
      cell: (info) => formatDate(info.getValue() as string | undefined),
    },
    {
      header: "Cree",
      accessorKey: "createdAt",
      cell: (info) => formatDate(info.getValue() as string | undefined),
    },
  ];

  const tokensTab: SubTab = {
    id: "tokens",
    label: "Tokens actifs",
    render: () => (
      <>
        <div class="panel-toolbar panel-toolbar--filter">
          <button
            type="button"
            class="btn btn--xs btn--info"
            onClick={() => qc.invalidateQueries({ queryKey: ["mcp-admin", "tokens"] })}
          >
            Rafraichir
          </button>
        </div>
        <DataTable
          rows={tokensQuery.data || []}
          columns={tokenColumns}
          emptyMessage="Aucun token actif."
          rowActions={(token) => (
            <button
              type="button"
              class="btn btn--xs btn--danger"
              disabled={Boolean(token.revoked)}
              onClick={() => void revokeToken(token)}
            >
              {token.revoked ? "Revoque" : "Revoquer"}
            </button>
          )}
        />
      </>
    ),
  };

  const createTab: SubTab = {
    id: "create",
    label: "Creer token",
    render: () => (
      <form class="panel-form" onSubmit={(e) => void submitCreate(e)}>
        <div class="panel-form-grid">
          <label class="panel-form-label">
            Label
            <input
              type="text"
              required
              placeholder="ex: claude-prod-1"
              value={labelInput()}
              onInput={(e) => setLabelInput(e.currentTarget.value)}
            />
          </label>
          <label class="panel-form-label">
            TTL (minutes, 5..480)
            <input
              type="number"
              min="5"
              max="480"
              value={ttlInput()}
              onInput={(e) => setTtlInput(Number(e.currentTarget.value) || 30)}
            />
          </label>
          <label class="panel-form-label">
            Session liee
            <select value={sessionCodeSel()} onChange={(e) => setSessionCodeSel(e.currentTarget.value)}>
              <option value="">- Aucune (lien session ulterieur) -</option>
              <For each={sessionsQuery.data || []}>
                {(s) => <option value={s.code}>{s.label ? `${s.code} (${s.label})` : s.code}</option>}
              </For>
            </select>
          </label>
        </div>

        <div class="panel-form-grid">
          <For each={SCOPE_SECTIONS}>
            {(section) => (
              <fieldset class="panel-fieldset">
                <legend>{section.title}</legend>
                <For each={section.fields}>
                  {(field) => {
                    const id = `${String(section.key)}.${field.name}`;
                    return (
                      <Show
                        when={field.kind === "bool"}
                        fallback={
                          <label class="panel-form-label">
                            {field.label}
                            <textarea
                              rows={2}
                              placeholder={field.kind === "list" ? field.placeholder || "Une entree par ligne" : ""}
                              value={scopeLists()[id] || ""}
                              onInput={(e) => setScopeLists((prev) => ({ ...prev, [id]: e.currentTarget.value }))}
                            />
                          </label>
                        }
                      >
                        <label class="panel-form-label panel-form-label--inline">
                          <input
                            type="checkbox"
                            checked={Boolean(scopeBools()[id])}
                            onChange={(e) =>
                              setScopeBools((prev) => ({
                                ...prev,
                                [id]: e.currentTarget.checked,
                              }))
                            }
                          />
                          <span> {field.label}</span>
                        </label>
                      </Show>
                    );
                  }}
                </For>
              </fieldset>
            )}
          </For>
        </div>

        <div class="panel-toolbar">
          <button type="submit" class="btn btn--info">
            Creer le token
          </button>
        </div>
      </form>
    ),
  };

  return (
    <>
      <PanelLayout title="Sessions MCP" subtabs={[tokensTab, createTab]} status={status()} />
      <Show when={plainToken()}>
        {(pt) => (
          <div
            class="panel-overlay"
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              inset: "0",
              background: "rgba(0,0,0,0.6)",
              "z-index": "9999",
              display: "flex",
              "align-items": "center",
              "justify-content": "center",
            }}
          >
            <div
              class="panel-card"
              style={{
                background: "var(--panel-bg, #1a1a1a)",
                padding: "20px",
                "max-width": "640px",
                width: "92%",
                "border-radius": "8px",
                "box-shadow": "0 12px 32px rgba(0,0,0,0.5)",
              }}
            >
              <h3>Token MCP cree</h3>
              <p>Copiez ce token MAINTENANT. Il ne sera plus jamais affiche en clair.</p>
              <textarea
                readonly
                rows={3}
                value={pt().token}
                style={{
                  width: "100%",
                  "font-family": "monospace",
                  "font-size": "12px",
                }}
              />
              <Show when={pt().expiresAt}>
                <p class="panel-muted">Expire : {formatDate(pt().expiresAt)}</p>
              </Show>
              <div class="panel-toolbar">
                <button type="button" class="btn btn--xs btn--info" onClick={() => void copyToken()}>
                  {copyLabel()}
                </button>
                <button type="button" class="btn btn--xs btn--danger" onClick={() => setPlainToken(null)}>
                  J'ai note, fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </Show>
    </>
  );
};

registerPanel("mcpSessions", McpSessionsPanel);

export default McpSessionsPanel;
